/**
 * Reparto de platos por área de despacho (demo localStorage).
 * Por cíclico + día: cantidades por celda (slotKey) y por área de despacho.
 */

export const STORAGE_DESPACHO_REPARTO = 'pc_despacho_reparto_v1';

function normalizeRecord(raw) {
  const cicloId = String(raw.cicloId || '');
  const diaKey = String(raw.diaKey || '').toLowerCase();
  const porSlot = raw.porSlot && typeof raw.porSlot === 'object' ? { ...raw.porSlot } : {};
  const updatedAt = raw.updatedAt ? String(raw.updatedAt) : new Date().toISOString();
  return { cicloId, diaKey, porSlot, updatedAt };
}

/** @returns {Array<{ cicloId: string, diaKey: string, porSlot: Record<string, Record<string, number>>, updatedAt: string }>} */
export function loadDespachoReparto() {
  try {
    const raw = localStorage.getItem(STORAGE_DESPACHO_REPARTO);
    if (!raw) return [];
    const p = JSON.parse(raw);
    if (!Array.isArray(p)) return [];
    return p.map(normalizeRecord).filter((r) => r.cicloId && r.diaKey);
  } catch {
    return [];
  }
}

export function saveDespachoReparto(list) {
  localStorage.setItem(STORAGE_DESPACHO_REPARTO, JSON.stringify(list));
}

export function getRepartoPorCicloYDia(cicloId, diaKey, list) {
  const cid = String(cicloId || '');
  const d = String(diaKey || '').toLowerCase();
  return (list || []).find((r) => r.cicloId === cid && r.diaKey === d) || null;
}

/**
 * @param {{ cicloId: string, diaKey: string, porSlot: Record<string, Record<string, string|number>> }} partial
 */
export function upsertDespachoReparto(partial) {
  const list = loadDespachoReparto();
  const cid = String(partial.cicloId || '');
  const dk = String(partial.diaKey || '').toLowerCase();
  if (!cid || !dk) return null;
  const idx = list.findIndex((r) => r.cicloId === cid && r.diaKey === dk);
  const prev = idx >= 0 ? list[idx] : null;
  /** Reemplazo completo del mapa por día (evita celdas huérfanas si cambia la plantilla). */
  const mergedPorSlot =
    partial.porSlot != null && typeof partial.porSlot === 'object'
      ? { ...partial.porSlot }
      : { ...(prev?.porSlot || {}) };
  const n = normalizeRecord({
    ...prev,
    cicloId: cid,
    diaKey: dk,
    porSlot: mergedPorSlot,
    updatedAt: new Date().toISOString(),
  });
  if (idx >= 0) list[idx] = n;
  else list.push(n);
  saveDespachoReparto(list);
  return getRepartoPorCicloYDia(cid, dk, list);
}

/** Suma asignada a un slot (todas las áreas). */
export function sumaAsignacionesSlot(porSlot, slotKey, areaIds) {
  const byArea = porSlot?.[slotKey];
  if (!byArea || typeof byArea !== 'object') return 0;
  let s = 0;
  for (const aid of areaIds) {
    const k = String(aid);
    const n = Math.max(0, parseInt(String(byArea[k] ?? '').replace(/\D/g, ''), 10) || 0);
    s += n;
  }
  return s;
}

/**
 * Reparto equitativo en enteros: reparte `total` entre `areaIds.length` áreas.
 * @param {string[]} areaIds
 * @returns {Record<string, number>}
 */
export function repartoEquitativo(total, areaIds) {
  const ids = (areaIds || []).map((x) => String(x));
  const n = Math.max(0, Math.floor(Number(total) || 0));
  const k = ids.length;
  if (k === 0 || n === 0) return Object.fromEntries(ids.map((id) => [id, 0]));
  const base = Math.floor(n / k);
  let rem = n - base * k;
  const out = {};
  for (const id of ids) {
    out[id] = base + (rem > 0 ? 1 : 0);
    if (rem > 0) rem -= 1;
  }
  return out;
}
